import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import isEqual from 'lodash/isEqual'
import { TableStore } from 'shared/ui/Table'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { toastStore } from 'shared/ui'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { DayjsFormats, getBlob, getContentType, getFileWithTruncatedName, logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { RequestLoadData } from 'shared/api/lib'
import {
  hasGoogleDocLiveDocument,
  hasGoogleSheetLiveDocument,
  KnowledgeBaseApi,
  KnowledgeBaseDocument,
  KnowledgeBaseDocumentStatus,
  type IParamsKnowledgeBaseDocuments,
  type IResponseKnowledgeBaseDocuments,
} from 'entities/KnowledgeBase'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { downloadWithToast } from 'features/FileDownload'

import {
  FORMAT_NOT_SUPPORTED,
  MAX_FILE_SIZE,
  MAX_FILES,
  STORAGE_IS_FULL,
  UPLOAD_LIMIT_EXCEEDED,
  UPLOAD_SIZE_ERROR,
} from 'pages/settings/pages/knowledgeBase/lib/constants'

import { getDescriptionForDeleteModal } from 'pages/settings/pages/knowledgeBase/lib/getDescriptionForDeleteModal'
import styles from 'pages/settings/pages/knowledgeBase/pages/knowledgeBaseView/styles.module.scss'
import type { KnowledgeBaseNotificationStore } from './knowledgeBaseNotificationStore'
import type { KnowledgeBaseStore } from './knowledgeBaseStore'
import { KnowledgeBaseDetailsModal } from '../pages/knowledgeBaseView/ui/DocumentsList/ui/KnowledgeBaseDetailsModal'
import { KnowledgeBaseCreateDocumentStore } from './knowledgeBaseCreateDocumentStore'
import { KnowledgeBaseUrlStore } from './knowledgeBaseUrlStore'

export class KnowledgeBaseDocumentsStore {
  private _total = 0
  private _page = 1
  private _limit = 10
  private _search = ''

  private _documentsMap = new Map<number, KnowledgeBaseDocument>()
  private _isDocumentsIndexingRetryLoading = false

  private _isDocumentsUploading = false
  private _uploadInProgressIds = new Set<number>()
  private _uploadedCount = 0

  private readonly _initialParams: IParamsKnowledgeBaseDocuments | null = null

  private _disposeLoadKnowledgeBaseDocuments: IReactionDisposer | null = null
  private _disposeChangeParams: IReactionDisposer | null = null

  tableStore = new TableStore<KnowledgeBaseDocument>({
    element: 'document',
  })
  createDocumentStore: KnowledgeBaseCreateDocumentStore
  urlStore: KnowledgeBaseUrlStore

  constructor(
    private _knowledgeBaseStore: KnowledgeBaseStore,
    private _notificationStore: KnowledgeBaseNotificationStore,
    private _entities: IEntitiesStore
  ) {
    this._initialParams = this._knowledgeBaseDocumentsRequestParams
    this.createDocumentStore = new KnowledgeBaseCreateDocumentStore(this)
    this.urlStore = new KnowledgeBaseUrlStore(this)
    makeAutoObservable(this)

    this._reactionLoadKnowledgeBaseDocuments()
    this._reactionChangeParams()
  }

  private _reactionChangeParams = () => {
    this._disposeChangeParams?.()
    this._disposeChangeParams = reaction(
      () => this._paramsWithoutPage,
      () => {
        this._requestLoadDocuments.setLoading(true)
        this._page = 1
      }
    )
  }

  private _reactionLoadKnowledgeBaseDocuments = () => {
    this._disposeLoadKnowledgeBaseDocuments?.()
    this._disposeLoadKnowledgeBaseDocuments = reaction(
      () => this._knowledgeBaseDocumentsRequestParams,
      () => this._requestLoadDocuments.debounceLoadData()
    )
  }

  private _setDocuments = ({ data, meta }: IResponseKnowledgeBaseDocuments) => {
    this._documentsMap.clear()
    data.forEach((documentResponse) => {
      this._documentsMap.set(documentResponse.id, new KnowledgeBaseDocument(documentResponse))
    })
    this._page = meta.current_page
    this._total = meta.total
  }

  get knowledgeBase() {
    return this._knowledgeBaseStore.knowledgeBase
  }

  get notificationStore() {
    return this._notificationStore
  }

  private _requestLoadDocuments = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      const { data } = await KnowledgeBaseApi.getKnowledgeBaseDocuments(
        this._knowledgeBaseDocumentsRequestParams,
        {
          cancelToken: cancelRequest.token,
        }
      )

      this._setDocuments(data)
    },
  })

  loadDocuments = async () => {
    return this._requestLoadDocuments.loadData()
  }

  removeDocument = (id: number) => {
    this._documentsMap.delete(id)
  }

  upsertDocument = (document: KnowledgeBaseDocument) => {
    if (this._isInitialParams) {
      this._documentsMap.set(document.id, document)
    }
  }

  handleUploadingDocument = (document: KnowledgeBaseDocument, isUploadCompleted = false) => {
    this.upsertDocument(document)

    if (isUploadCompleted) {
      if (this._uploadInProgressIds.has(document.id)) {
        this._uploadInProgressIds.delete(document.id)
      }
    } else {
      this._uploadInProgressIds.add(document.id)
      this._uploadedCount++
      return
    }

    if (this._isUploadFinished) {
      void this.loadDocuments()
      toastStore.add({
        type: 'success',
        title: `${this._uploadedCount} file(s) uploaded`,
      })
      this._uploadedCount = 0
    }
  }

  onRetryIndexation = async (ids: number[]) => {
    runInAction(() => {
      this._isDocumentsIndexingRetryLoading = true
    })
    try {
      await KnowledgeBaseApi.retryDocumentsIndexation(ids)
    } catch (e) {
      logger.error(e)
      toastStore.add({
        type: 'error',
        title: 'Something went wrong',
      })
    } finally {
      runInAction(() => {
        this._isDocumentsIndexingRetryLoading = false
      })
    }
  }

  onUploadDocuments = async (files: File[]) => {
    const knowledgeBase = this._knowledgeBaseStore.knowledgeBase
    runInAction(() => {
      this._isDocumentsUploading = true
    })
    try {
      if (!knowledgeBase) {
        toastStore.add({
          type: 'warning',
          title: 'Knowledge base not found',
        })
        return
      }

      if (files.length > MAX_FILES) {
        this._notificationStore.addWarning(UPLOAD_LIMIT_EXCEEDED)
        return
      }
      if (
        knowledgeBase.documentsCount + files.length > MAX_FILES ||
        this._documentsMap.size + files.length > MAX_FILES
      ) {
        this._notificationStore.addError(STORAGE_IS_FULL)
        return
      }
      const hasExceededSizeLimit = files.some((file) => file.size > MAX_FILE_SIZE)
      if (hasExceededSizeLimit) {
        this._notificationStore.addError(UPLOAD_SIZE_ERROR)
        return
      }

      const validFiles: { file: File; contentType: string }[] = []
      const invalidFiles: File[] = []

      for (const file of files) {
        const contentType = await getContentType(file)
        if (this._entities.attachmentStore.extendedFilesAccept.includes(contentType)) {
          validFiles.push({ file, contentType })
        } else {
          invalidFiles.push(file)
        }
      }

      if (invalidFiles.length === files.length) {
        this._notificationStore.addError(FORMAT_NOT_SUPPORTED)
        return
      }
      if (invalidFiles.length > 0) {
        const validCount = validFiles.length
        const invalidCount = invalidFiles.length
        this._notificationStore.addWarning(
          `${validCount} files from ${files.length} are being uploaded. The format of ${invalidCount} file isn’t supported by the platform and can’t be uploaded.`
        )
      }

      if (validFiles.length) {
        const uploadPromises = validFiles.map(async ({ file, contentType }) => {
          try {
            const preparedFile = getFileWithTruncatedName(file)
            const { data } = await KnowledgeBaseApi.getUploadDocumentLink({
              name: preparedFile.name,
              content_type: contentType,
            })
            const { link, document: docResponse } = data

            void KnowledgeBaseApi.uploadDocument(link.url, preparedFile)

            const document = new KnowledgeBaseDocument(docResponse)
            this.handleUploadingDocument(document)
          } catch (e) {
            if (e instanceof AxiosError && e.errors.getError('message')) {
              this._notificationStore.addError(e.errors.getError('message'))
            }
          }
        })

        await Promise.all(uploadPromises)
      }
    } catch (err) {
      this._notificationStore.addError('Unknown error during upload.')
    } finally {
      runInAction(() => {
        this._isDocumentsUploading = false
      })
    }
  }

  deleteLockedDocsModal = (count: number) => {
    const modalId = nanoid()
    const countText = `document${count > 1 ? 's' : ''}`
    const isShowCount = count > 1

    modalStore.addModal({
      id: modalId,
      type: ModalTypeList.ALERT,
      showCloseButton: false,
      title: `Can't delete ${isShowCount ? count : ''} ${countText} now`,
      desc: `Someone else is currently editing ${countText}. Please try again later.`,
      primaryAction: {
        text: 'Done',
        onAction: () => {
          modalStore.closeModal(modalId)
        },
      },
    })
  }

  onDeleteDocuments = async (ids: number[]) => {
    if (!this._knowledgeBaseStore.knowledgeBase) {
      toastStore.add({
        type: 'warning',
        title: 'Knowledge base not found',
      })
      return
    }
    const deleteModalId = nanoid()

    const handleCloseDeleteModal = () => {
      modalStore.removeModal(deleteModalId)
    }

    const handleDeleteDocuments = async () => {
      try {
        const { data } = await KnowledgeBaseApi.delete(ids)
        const deletedCount = data.deleted.length
        const skippedCount = data.skipped.length

        toastStore.add({
          type: 'success',
          title: deletedCount > 1 ? deletedCount + ' files deleted' : 'File deleted',
        })

        const isLockedDocs = data.skipped?.some((skipped) => skipped?.error_type === 'locked')

        if (skippedCount > 0 && isLockedDocs) {
          this.deleteLockedDocsModal(skippedCount)
        }

        ids.forEach((id) => {
          const doc = this._documentsMap.get(id)
          if (doc) {
            this.tableStore.toggleSelected(doc, false)
          }
          this.removeDocument(id)
        })

        void Promise.all([this._knowledgeBaseStore.loadKnowledgeBase(), this.loadDocuments()])
      } catch (e) {
        if (e instanceof AxiosError && e.response?.status === 403) {
          this.deleteLockedDocsModal(1)
        }
        logger.error(e)
      } finally {
        handleCloseDeleteModal()
      }
    }

    try {
      const { data } = await KnowledgeBaseApi.canDeleteDocument(ids)

      modalStore.addModal({
        id: deleteModalId,
        showHeader: true,
        showCloseButton: false,
        showCloseIcon: true,
        width: 280,
        type: ModalTypeList.ALERT,
        title: ids.length > 1 ? `Delete ${ids.length} files?` : 'Delete file?',
        desc: getDescriptionForDeleteModal(ids, this._knowledgeBaseStore.knowledgeBase, data),
        primaryAction: {
          text: 'Delete',
          onAction: handleDeleteDocuments,
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: handleCloseDeleteModal,
        },
        onClose: handleCloseDeleteModal,
      })
    } catch (e) {
      logger.error(e)
    }
  }

  onReconnectGoogleDocument = async (document: KnowledgeBaseDocument) => {
    if (!document.getIsLiveDocument()) return

    try {
      await KnowledgeBaseApi.reconnectLiveDocument(document.id)
      await this.loadDocuments()
    } catch (error) {
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Something went wrong',
      })
    }
  }

  onDownloadDocument = async (id: number) => {
    await downloadWithToast(
      async () => {
        const { data } = await KnowledgeBaseApi.getDownloadLink(id)

        const document = this._documentsMap.get(id)
        const name = document?.name || String(id)

        const blob = await getBlob(data.link)
        if (!blob) {
          throw new Error('Failed to download Blob from ' + data.link)
        }

        return { blob, name }
      },
      () => this.onDownloadDocument(id)
    )
    this.onTrackDownloadMedia([Number(id)])
  }

  private _trackPhiFileViewed = (fileId: number) => {
    if (!this._entities.organizationStore.isHipaaCompliant) return
    this._entities.organizationStore.handleTrackOrganizationActivity({
      event: 'phi.file.viewed',
      data: {
        file_id: fileId,
      },
    })
  }

  onPreview = async (item: KnowledgeBaseDocument) => {
    if (item.getIsLiveDocument()) {
      const externalUrl = item.getExternalUrl()
      if (!externalUrl) return

      window.open(externalUrl, '_blank', 'noopener,noreferrer')
      this._trackPhiFileViewed(item.id)
      return
    }

    try {
      const {
        data: { link },
      } = await KnowledgeBaseApi.getDownloadLink(item.id)

      modalStore.addModal({
        id: nanoid(),
        width: 1000,
        ModalContentProps: { item, link },
        ModalContent: KnowledgeBaseDetailsModal,
        title: item.name,
        className: styles.modalWrapper,
        pureContent: true,
        fullHeight: true,
        position: 'right',
      })

      this._trackPhiFileViewed(item.id)
    } catch (e) {
      logger.error(e)
    }
  }

  onBulkDownloadDocuments = async (ids: number[]) => {
    await downloadWithToast(
      async () => {
        const { data } = await KnowledgeBaseApi.getBulkDownloadLink(ids)

        const name =
          ids.length === 1
            ? this._documentsMap.get(ids[0])?.name || 'file'
            : 'Salesmsg_' + uiStore.dayjs(new Date()).format(DayjsFormats.downloadFile)

        const blob = await getBlob(data.link)
        if (!blob) {
          throw new Error('Failed to download Blob from ' + data.link)
        }

        return { blob, name }
      },
      () => this.onBulkDownloadDocuments(ids)
    )
    this.onTrackDownloadMedia(ids.map((id) => Number(id)))
  }

  onTrackDownloadMedia = (ids: number[]) => {
    if (this._entities.organizationStore.isHipaaCompliant) {
      this._entities.organizationStore.handleTrackOrganizationActivity({
        event: 'phi.file.downloaded',
        data: {
          file_ids: ids,
        },
      })
    }
  }

  onPaginationChange = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
  }

  private get _knowledgeBaseDocumentsRequestParams(): IParamsKnowledgeBaseDocuments {
    return {
      page: this._page,
      ...this._paramsWithoutPage,
    }
  }

  private get _paramsWithoutPage() {
    return {
      limit: this._limit,
      'filters[name]': this._search,
    }
  }

  private get _isInitialParams() {
    return isEqual(this._initialParams, this._knowledgeBaseDocumentsRequestParams)
  }

  private get _isUploadFinished() {
    return this._uploadInProgressIds.size === 0 && this._uploadedCount !== 0
  }

  get documents() {
    return Array.from(this._documentsMap.values()).sort(
      (a, b) =>
        Number(b.getIsUploading()) - Number(a.getIsUploading()) ||
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }

  get isDocumentsLoading() {
    return this._requestLoadDocuments.loading
  }

  get isDocumentsUploading() {
    return this._isDocumentsUploading
  }

  get page() {
    return this._page
  }

  get limit() {
    return this._limit
  }

  get total() {
    return this._total
  }

  get search() {
    return this._search
  }

  get isEmpty() {
    return !this.isDocumentsLoading && !this._documentsMap.size && !this._search
  }

  get documentIdsToRetry() {
    return this.documents
      .filter((document) => document.status === KnowledgeBaseDocumentStatus.IndexationFailed)
      .map((document) => document.id)
  }

  get hasGoogleSheetLiveDocument() {
    return hasGoogleSheetLiveDocument(this.documents)
  }

  get hasGoogleDocLiveDocument() {
    return hasGoogleDocLiveDocument(this.documents)
  }

  get isDocumentsIndexingRetryLoading() {
    return this._isDocumentsIndexingRetryLoading
  }

  onSearchChange = (value: string) => {
    this._search = value
  }

  dispose = () => {
    this._disposeLoadKnowledgeBaseDocuments?.()
    this._disposeChangeParams?.()
    this._requestLoadDocuments.dispose()
  }
}
